import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Eye } from "lucide-react";
import { Logo } from "@/components/Logo";
import { viewProtected } from "@/lib/files";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

const FIELDS = [
  ["firm_legal_name", "Razón social o nombre profesional del despacho"],
  ["nif_cif", "NIF / CIF"],
  ["professional_address", "Domicilio profesional"],
  ["representative_name", "Representante (nombre y apellidos)"],
  ["representative_role", "Cargo o condición del representante"],
  ["professional_email", "Correo electrónico profesional"],
];

export default function Acuerdo() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ firm_legal_name: "", nif_cif: "", professional_address: "", representative_name: "", representative_role: "", professional_email: "" });
  const [d1, setD1] = useState(false);
  const [d2, setD2] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.email) setF((p) => ({ ...p, professional_email: p.professional_email || user.email, representative_name: p.representative_name || user.name || "" }));
  }, [user]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const complete = FIELDS.every(([k]) => String(f[k] || "").trim());
  const ready = complete && d1 && d2;

  const accept = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await api.post("/agreement/accept", { ...f, decl_agreement: d1, decl_authority: d2 });
      toast.success("Acuerdo aceptado correctamente.");
      await refresh();
      navigate("/inicio");
    } catch (e) {
      toast.error(apiError(e, "No se pudo registrar la aceptación."));
    } finally {
      setBusy(false);
    }
  };

  const viewMaster = () => viewProtected("/agreement/master").catch(() => toast.error("No se pudo abrir el acuerdo."));

  return (
    <div style={{ minHeight: "100vh", background: "var(--ivory)" }}>
      <div style={{ height: 3, background: "var(--burgundy)" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "clamp(24px,4vw,52px) 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 26 }}>
          <Logo height={38} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>Versión {`1.0`} · septiembre de 2026</span>
        </div>

        <div className="ddp-card" style={{ padding: "clamp(24px,4vw,40px)" }} data-testid="agreement-screen">
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
            <ShieldCheck size={22} style={{ color: "var(--burgundy)" }} />
            <h1 className="ddp-serif" style={{ fontSize: "clamp(24px,3vw,34px)", color: "var(--burgundy-dark)", margin: 0 }}>Acuerdo de tratamiento de datos y confidencialidad</h1>
          </div>
          <p style={{ fontSize: 14.5, color: "#3D3834", lineHeight: 1.6, marginTop: 6 }}>
            Para acceder al área de asuntos y remitir documentación, el despacho debe formalizar el acuerdo que regula el tratamiento de los expedientes por DDP.
          </p>

          <div style={{ margin: "18px 0" }}>
            <button onClick={viewMaster} data-testid="view-master" className="ddp-btn ddp-btn-secondary ddp-btn-sm"><Eye size={15} /> Ver / descargar el acuerdo completo (PDF)</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16 }}>
            {FIELDS.map(([k, label]) => (
              <div key={k}>
                <label className="ddp-label">{label}</label>
                <input className="ddp-input" data-testid={`agr-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} />
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 22 }}>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" data-testid="agr-decl1" checked={d1} onChange={(e) => setD1(e.target.checked)} style={{ marginTop: 3, accentColor: "var(--burgundy)" }} />
              <span style={{ fontSize: 13.5, color: "#3D3834", lineHeight: 1.5 }}>He leído y acepto íntegramente el presente Acuerdo de Encargo de Tratamiento de Datos y Confidencialidad, incluidos sus anexos.</span>
            </label>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" data-testid="agr-decl2" checked={d2} onChange={(e) => setD2(e.target.checked)} style={{ marginTop: 3, accentColor: "var(--burgundy)" }} />
              <span style={{ fontSize: 13.5, color: "#3D3834", lineHeight: 1.5 }}>Declaro que dispongo de facultades suficientes para aceptar este acuerdo en nombre del Despacho identificado anteriormente.</span>
            </label>
          </div>

          <button onClick={accept} disabled={!ready || busy} data-testid="agr-accept" className="ddp-btn ddp-btn-primary" style={{ marginTop: 24, minWidth: 220 }}>
            {busy ? "Procesando…" : "Aceptar y continuar"}
          </button>
        </div>
      </div>
    </div>
  );
}
